/* eslint-disable import/no-named-as-default */
/* eslint-disable import/no-named-as-default-member */
import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Box, Grid, InputAdornment, InputBase, makeStyles, Typography, Divider,
  Button as MuiButton, Dialog, DialogContent, Badge, Tooltip, IconButton,
} from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import LinkOffIcon from '@material-ui/icons/LinkOff';
import SearchIcon from '@material-ui/icons/Search';
import Button from '../../controls/Button';
import { CloseIcon, DocumentIcon } from '../../../assets/icons';
import { documentActions } from '../../../redux/actions';
import SetNewDocumentModal from '../../Documents/SetNewDocumentModal/SetNewDocumentModal';

const useStyles = makeStyles((theme) => ({
  linkDocuments: {
    color: '#FFFFFF',
    fontSize: '10px',
    fontWeight: 600,
    paddingLeft: '10px',
    textDecoration: 'none',
    textTransform: 'uppercase',
  },
  documentIcon: {
    color: '#FFFFFF',
    height: '13px',
    width: '13px',
  },
  iconHeader: {
    color: '#DADADA',
    height: '51px',
    width: '51px',
  },
  iconBody: {
    color: '#DADADA',
    height: '45px',
    width: '45px',
  },
  textHeader: {
    color: theme.palette.primary.main,
    fontSize: '14px',
    fontWeight: 700,
    letterSpacing: '0.15px',
  },
  itemTitle: {
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.15px',
    textAlign: 'center',
    marginTop: '4px',
  },
  itemVersion: {
    fontWeight: 400,
    fontSize: '11px',
    letterSpacing: '0.15px',
    textAlign: 'center',
  },
  resultContainer: {
    maxHeight: '219px',
    overflowY: 'auto',
    overflowX: 'hidden',
    scrollbarColor: '#999999',
    scrollbarWidth: 'thin',
    '&::-webkit-scrollbar': {
      width: '0.3em',
      paddingLeft: '10px',
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.00)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(0,0,0,.2)',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(0,0,0,.4)',
    },
  },
  resultGrid: {
    display: 'grid',
    gridGap: '6px',
    gridTemplateColumns: 'repeat(auto-fill, 120px)',
    justifyContent: 'space-evenly',
  },
  iconSearch: {
    marginLeft: '8px',
  },
  unlinkDocumentContent: {
    width: '280px',
    paddingTop: '20px',
    fontSize: '12px',
    fontWeight: 400,
  },
  iconUnlink: {
    background: '#FFF',
    padding: '0px',
    '&:hover': {
      backgroundColor: '#F22525',
    },
  },
  iconButtonUnlink: {
    padding: '0px',
    '&:hover': {
      color: '#FFF',
    },
  },
  closeIcon: {
    width: '14px',
    height: '13px',
    cursor: 'pointer',
  },
  unlinkDocumentTitle: {
    fontSize: '14px',
    fontWeight: 700,
    textAlign: 'center',
  },
  centeredContainer: {
    alignContent: 'center',
    alignItems: 'center',
    display: 'grid',
    justifyContent: 'center',
    justifyItems: 'center',
  },
  centeredContainerButton: {
    alignItems: 'center',
    backgroundColor: theme.palette.primary.main,
    display: 'flex',
    height: '32px',
    justifyContent: 'center',
  },
}));

const ProcessDocumentDetail = ({ documents, processId }) => {
  const intl = useIntl();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { roles } = useSelector((state) => state.authentication.user);
  const isAdmin = roles.includes('Admin');
  const [searchText, setSearchText] = useState('');
  const [searchResult, setSearchResult] = useState(documents);
  const [openDocumentModal, setOpenDocumentModal] = useState(false);
  const [openUnlinkDialog, setOpenUnlinkDialog] = useState(false);
  const [documentToUnlink, setDocumentToUnlink] = useState();
  const documentsLabel = intl.formatMessage({ id: 'document.linkedDocument', defaultMessage: '{count, plural, =0 {No hay documentos asociados} one {# Documento} other {# Documentos}}' }, { count: documents.length });
  const addDocumentText = intl.formatMessage({ id: 'document.addToProcess', defaultMessage: 'AGREGAR DOCUMENTO AL PROCESO' });

  useEffect(() => {
    if (searchText) {
      const filtered = documents.filter(
        (doc) => doc.title.toLowerCase().startsWith(searchText.toLowerCase()),
      );
      return setSearchResult(filtered);
    }
    return setSearchResult(documents);
  }, [searchText, documents]);

  function handleTextChange({ target: { value } }) {
    setSearchText(value);
  }

  function toggleOpenDocumentModal() {
    return setOpenDocumentModal((prevValue) => !prevValue);
  }

  function toggleOpenUnlinkDialog(id) {
    setOpenUnlinkDialog(!openUnlinkDialog);
    setDocumentToUnlink(id);
  }

  function unlinkDocument(documentId) {
    dispatch(documentActions.unlinkProcess(processId, documentId));
    setOpenUnlinkDialog(!openUnlinkDialog);
  }

  return (
    <Box width={291} bgcolor="#FCFCFC" m={1} ml={0}>
      <Box className={classes.centeredContainer} height={105}>
        <InsertDriveFileIcon className={classes.iconHeader} />
        <Typography display="block" className={classes.textHeader}>
          {documentsLabel}
        </Typography>
      </Box>
      <Divider />
      <Box minHeight={263}>
        <Box bgcolor="#FFFFFF" borderRadius={18} mx="auto" mt={2} mb={2.5} width={250}>
          <InputBase
            classes={{ adornedStart: classes.iconSearch }}
            placeholder="Buscar"
            value={searchText}
            onChange={handleTextChange}
            startAdornment={(
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )}
          />
        </Box>
        <Box className={classes.resultContainer}>
          <Grid container className={classes.resultGrid}>
            {searchResult.map((document) => {
              const actualVersion = document.versions ? document.versions.find(
                (version) => version.id === document.actualDocumentVersionId,
              ) : '';
              return (
                <Grid item key={document.id}>
                  <Box className={classes.centeredContainer} pb={0.5}>
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      classes={{ badge: classes.iconUnlink }}
                      badgeContent={(
                        <Tooltip title={<FormattedMessage id="app.unlink" defaultMessage="DESVINCULAR" />}>
                          <IconButton
                            classes={{ root: classes.iconButtonUnlink }}
                            onClick={() => toggleOpenUnlinkDialog(document.id)}
                          >
                            <LinkOffIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    >
                      <Link to={`/documents/${document.id}/${actualVersion.id}`} style={{ textDecoration: 'none' }}>
                        <InsertDriveFileIcon className={classes.iconBody} />
                      </Link>
                    </Badge>
                    <Typography display="block" className={classes.itemTitle}>
                      {document.title}
                    </Typography>
                    <Typography display="block" className={classes.itemVersion}>
                      {actualVersion.versionName}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
      {isAdmin && (
      <Box className={classes.centeredContainerButton}>
        <DocumentIcon className={classes.documentIcon} display="inline" />
        <MuiButton
          color="primary"
          onClick={toggleOpenDocumentModal}
          display="inline"
          className={classes.linkDocuments}
        >
          {addDocumentText}
        </MuiButton>
      </Box>
      )}
      <Dialog
        open={openDocumentModal}
        onClose={toggleOpenDocumentModal}
        aria-labelledby="form-dialog-title"
      >
        <SetNewDocumentModal
          isOpen={openDocumentModal}
          setIsOpen={toggleOpenDocumentModal}
          processId={processId}
        />
      </Dialog>
      <Dialog
        open={openUnlinkDialog}
        onClose={toggleOpenUnlinkDialog}
        aria-labelledby="form-dialog-title"
        fullWidth
        maxWidth="xs"
      >
        <DialogContent>
          <Box display="flex" justifyContent="flex-end">
            <CloseIcon className={classes.closeIcon} onClick={toggleOpenUnlinkDialog} />
          </Box>
          <Typography variant="subtitle1" className={classes.unlinkDocumentTitle}>
            <FormattedMessage id="document.unlinkDocument" defaultMessage="DESVINCULAR DOCUMENTO" />
          </Typography>
          <Box display="flex" align="center" justifyContent="center">
            <Typography variant="subtitle1" className={classes.unlinkDocumentContent}>
              <FormattedMessage id="document.unlinkProcessText" defaultMessage="Se dejará de utilizar el documento en éste proceso, pero no se eliminará del sistema, ni de los otros procesos donde esté asociado." />
            </Typography>
          </Box>
          <Box display="flex" justifyContent="center" mt={2} mb={2}>
            <Button
              onClick={toggleOpenUnlinkDialog}
              variant="outlined"
              text={<FormattedMessage id="app.cancel" defaultMessage="CANCELAR" />}
            />
            <Button
              onClick={() => unlinkDocument(documentToUnlink)}
              text={<FormattedMessage id="app.unlink" defaultMessage="DESVINCULAR" />}
            />
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

ProcessDocumentDetail.propTypes = {
  documents: PropTypes.arrayOf((PropTypes.shape())),
  processId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

ProcessDocumentDetail.defaultProps = {
  documents: [],
  processId: null,
};

export default ProcessDocumentDetail;
